import React, { useEffect, useMemo, useState } from "react";
import {
  Users,
  BadgeCheck,
  SunMedium,
  Wallet,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { notify } from "../utils/notify";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import StatCard from "../components/StatCard";
import UIButton from "../components/UIButton";
import UILabel from "../components/UILabel";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";

export default function Workers({ t = {}, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();
  const isGerman = lang === "de" || t?.german === "Deutsch";

  const [name, setName] = useState("");
  const [role, setRole] = useState("Worker");
  const [phone, setPhone] = useState("");
  const [dailyRate, setDailyRate] = useState("");
  const [status, setStatus] = useState("Active");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileSection, setMobileSection] = useState("form");

  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const workerRoleLabels = {
    Worker: t?.workerRoleWorker || (isGerman ? "Arbeiter" : "Punëtor"),
    Manager: t?.workerRoleManager || (isGerman ? "Leiter" : "Menaxher"),
    Driver: t?.workerRoleDriver || (isGerman ? "Fahrer" : "Shofer"),
    Electrician:
      t?.workerRoleElectrician || (isGerman ? "Elektriker" : "Elektricist"),
    Mason: t?.workerRoleMason || (isGerman ? "Maurer" : "Murator"),
  };

  const workerStatusLabels = {
    Active: t?.workerStatusActive || (isGerman ? "Aktiv" : "Aktiv"),
    Vacation: t?.workerStatusVacation || (isGerman ? "Urlaub" : "Pushim"),
    Inactive: t?.workerStatusInactive || (isGerman ? "Inaktiv" : "Jo aktiv"),
  };

  const historyTitle =
    t?.workersHistoryTitle ||
    t?.workersHistory ||
    (isGerman ? "Mitarbeiterverlauf" : "Historia e punëtorëve");

  const recordsLabel = t?.records || (isGerman ? "Einträge" : "regjistrime");
  const prevLabel = t?.prev || (isGerman ? "Zurück" : "Mbrapa");
  const nextLabel = t?.next || (isGerman ? "Weiter" : "Para");
  const pageLabel = t?.page || (isGerman ? "Seite" : "Faqja");
  const ofLabel = t?.of || (isGerman ? "von" : "nga");

  const loadWorkers = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "workers"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setItems(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.error("Error loading workers:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkers();
  }, []);

  const activeCount = useMemo(
    () => items.filter((item) => item.status === "Active").length,
    [items]
  );

  const vacationCount = useMemo(
    () => items.filter((item) => item.status === "Vacation").length,
    [items]
  );

  const totalPayrollEstimate = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.dailyRate) || 0), 0),
    [items]
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const resetForm = () => {
    setName("");
    setRole("Worker");
    setPhone("");
    setDailyRate("");
    setStatus("Active");
    setStartDate(new Date().toISOString().slice(0, 10));
    setEditId(null);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setName(item.name || "");
    setRole(item.role || "Worker");
    setPhone(item.phone || "");
    setDailyRate(item.dailyRate || "");
    setStatus(item.status || "Active");
    setStartDate(item.startDate || new Date().toISOString().slice(0, 10));
    setMobileSection("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const confirmText =
      t?.confirmDeleteWorker ||
      (isGerman
        ? "Möchtest du diesen Mitarbeiter wirklich löschen?"
        : "A dëshiron ta fshish këtë punëtor?");

    if (!window.confirm(confirmText)) return;

    try {
      await deleteDoc(doc(db, "workers", id));
      notify.success(isGerman ? "Erfolgreich gelöscht" : "U fshi me sukses");

      if (editId === id) resetForm();
      await loadWorkers();

      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting worker:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name) return;

    try {
      setSaving(true);

      const entry = {
        name,
        role,
        phone: phone || "",
        dailyRate: Number(dailyRate) || 0,
        status,
        startDate,
      };

      if (editId) {
        await updateDoc(doc(db, "workers", editId), entry);
        notify.success(
          t?.updateSuccess ||
            (isGerman ? "Erfolgreich aktualisiert" : "U përditësua me sukses")
        );
      } else {
        entry.createdAt = serverTimestamp();
        await addDoc(collection(db, "workers"), entry);
        notify.success(t?.savedSuccessfully || "Punëtori u ruajt me sukses");
      }

      resetForm();
      await loadWorkers();
      setCurrentPage(1);
    } catch (error) {
      console.error("Error saving worker:", error);
      notify.error(
        t?.saveErrorWorker ||
          (isGerman ? "Fehler beim Speichern." : "Gabim gjatë ruajtjes.")
      );
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(Number(value) || 0);

  const getStatusBadge = (value) => {
    const base =
      "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";
    if (value === "Active") {
      return theme === "dark"
        ? `${base} bg-emerald-950 text-emerald-300`
        : `${base} bg-emerald-100 text-emerald-700`;
    }
    if (value === "Vacation") {
      return theme === "dark"
        ? `${base} bg-amber-950 text-amber-300`
        : `${base} bg-amber-100 text-amber-700`;
    }
    return theme === "dark"
      ? `${base} bg-slate-800 text-slate-300`
      : `${base} bg-slate-100 text-slate-700`;
  };

  const inputClass = `h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-slate-50 text-slate-900 focus:bg-white"
  }`;

  return (
    <PageShell>
      <SectionHeading
        title={t?.workersTitle || "Punëtorë"}
        subtitle={
          t?.workersSubtitle ||
          (isGerman
            ? "Verwalte Mitarbeiter, Rollen, Tageslohn und Arbeitsstatus an einem Ort."
            : "Menaxho punëtorët, rolet, pagesën ditore dhe statusin e punës në një vend.")
        }
      />

      <div className="grid min-w-0 grid-cols-2 gap-2.5 xl:grid-cols-4">
        <StatCard
          title={t?.totalWorkers || "Totali i punëtorëve"}
          value={String(items.length)}
          subtitle={
            t?.totalWorkersSubtitle ||
            (isGerman
              ? "Gesamtanzahl registrierter Mitarbeiter"
              : "Numri total i punëtorëve të regjistruar")
          }
          icon={Users}
          accent="blue"
        />
        <StatCard
          title={t?.activeWorkers || (isGerman ? "Aktiv" : "Aktiv")}
          value={String(activeCount)}
          subtitle={
            t?.activeWorkersSubtitle ||
            (isGerman ? "Aktive Mitarbeiter" : "Punëtorë aktivë")
          }
          icon={BadgeCheck}
          accent="emerald"
        />
        <StatCard
          title={t?.vacationWorkers || (isGerman ? "Urlaub" : "Pushim")}
          value={String(vacationCount)}
          subtitle={
            t?.vacationWorkersSubtitle ||
            (isGerman ? "Mitarbeiter im Urlaub" : "Punëtorë në pushim")
          }
          icon={SunMedium}
          accent="amber"
        />
        <StatCard
          title={
            t?.totalPayrollEstimate ||
            (isGerman ? "Lohnsumme / Tag" : "Totali / ditë")
          }
          value={formatCurrency(totalPayrollEstimate)}
          subtitle={
            t?.totalPayrollEstimateSubtitle ||
            (isGerman
              ? "Geschätzte Tageslohnsumme"
              : "Vlerësim i pagesës ditore")
          }
          icon={Wallet}
          accent="violet"
        />
      </div>

      <div className="xl:hidden">
        <PanelCard className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <UIButton
              type="button"
              onClick={() => setMobileSection("form")}
              theme={theme}
              variant={mobileSection === "form" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {editId
                ? t?.update || "Përditëso"
                : t?.addWorkerTitle || "Shto punëtor"}
            </UIButton>

            <UIButton
              type="button"
              onClick={() => setMobileSection("history")}
              theme={theme}
              variant={mobileSection === "history" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {historyTitle}
            </UIButton>
          </div>
        </PanelCard>
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[440px_minmax(0,1fr)]">
        <div className={mobileSection === "history" ? "hidden xl:block" : "block"}>
          <PanelCard>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h3
                  className={`text-xl font-bold tracking-tight ${
                    theme === "dark" ? "text-slate-100" : "text-slate-900"
                  }`}
                >
                  {editId
                    ? t?.update || "Përditëso"
                    : t?.addWorkerTitle || "Shto punëtor"}
                </h3>
                <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                  {t?.addWorkerSubtitle ||
                    (isGerman
                      ? "Erfasse neue Mitarbeiter mit Rolle, Tageslohn und Status."
                      : "Regjistro punëtorë të rinj me rol, pagesë ditore dhe status.")}
                </p>
              </div>

              {editId && (
                <UIButton
                  type="button"
                  onClick={resetForm}
                  theme={theme}
                  variant="softDanger"
                  size="sm"
                >
                  {t?.cancel || "Anulo"}
                </UIButton>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <UILabel>
                  {t?.workerName ||
                    (isGerman ? "Name des Mitarbeiters" : "Emri i punëtorit")}
                </UILabel>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={
                    t?.workerNamePlaceholder ||
                    (isGerman
                      ? "Z. B. Arben Krasniqi"
                      : "P.sh. Arben Krasniqi")
                  }
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <UILabel>{t?.role || (isGerman ? "Branche" : "Roli")}</UILabel>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Worker">{workerRoleLabels.Worker}</option>
                    <option value="Manager">{workerRoleLabels.Manager}</option>
                    <option value="Driver">{workerRoleLabels.Driver}</option>
                    <option value="Electrician">
                      {workerRoleLabels.Electrician}
                    </option>
                    <option value="Mason">{workerRoleLabels.Mason}</option>
                  </select>
                </div>

                <div>
                  <UILabel>{t?.status || "Statusi"}</UILabel>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Active">{workerStatusLabels.Active}</option>
                    <option value="Vacation">{workerStatusLabels.Vacation}</option>
                    <option value="Inactive">{workerStatusLabels.Inactive}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <UILabel>
                    {t?.phone || (isGerman ? "Telefon" : "Telefoni")}
                  </UILabel>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+49..."
                    className={inputClass}
                  />
                </div>

                <div>
                  <UILabel>
                    {t?.dailyRate || (isGerman ? "Tageslohn" : "Pagesa ditore")}
                  </UILabel>
                  <input
                    type="number"
                    step="0.01"
                    value={dailyRate}
                    onChange={(e) => setDailyRate(e.target.value)}
                    placeholder="0.00"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <UILabel>
                  {t?.startDate || (isGerman ? "Startdatum" : "Data e fillimit")}
                </UILabel>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={inputClass}
                />
              </div>

              <UIButton
                type="submit"
                disabled={saving}
                theme={theme}
                variant="primary"
                size="xl"
                className="w-full"
              >
                {saving
                  ? t?.savingWorker || "Duke ruajtur..."
                  : editId
                  ? t?.update || "Përditëso"
                  : t?.saveWorker || "Ruaj punëtorin"}
              </UIButton>
            </form>
          </PanelCard>
        </div>

        <PanelCard>
          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <h3
                className={`text-xl font-bold tracking-tight ${
                  theme === "dark" ? "text-slate-100" : "text-slate-900"
                }`}
              >
                {historyTitle}
              </h3>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                {t?.workersHistorySubtitle ||
                  (isGerman
                    ? "Sieh dir Rollen, Status und Tageslohn der Mitarbeiter an."
                    : "Shiko rolet, statusin dhe pagesën ditore të punëtorëve.")}
              </p>
            </div>
            <div
              className={`rounded-2xl px-3 py-2 text-sm font-semibold ${currentTheme.surfaceSoft} ${currentTheme.muted}`}
            >
              {items.length} {recordsLabel}
            </div>
          </div>

          <div className="flex-1">
            {loading ? (
              <div
                className={`flex min-h-[320px] items-center justify-center rounded-3xl border border-dashed text-center ${
                  theme === "dark"
                    ? "border-slate-700 bg-slate-900/60"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <p
                  className={`text-base font-semibold ${
                    theme === "dark" ? "text-slate-200" : "text-slate-700"
                  }`}
                >
                  {t?.loading || "Duke ngarkuar..."}
                </p>
              </div>
            ) : currentItems.length === 0 ? (
              <div
                className={`flex min-h-[320px] items-center justify-center rounded-3xl border border-dashed text-center ${
                  theme === "dark"
                    ? "border-slate-700 bg-slate-900/60"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div>
                  <p
                    className={`text-base font-semibold ${
                      theme === "dark" ? "text-slate-200" : "text-slate-700"
                    }`}
                  >
                    {t?.noWorkersYet ||
                      (isGerman
                        ? "Noch keine Mitarbeiter vorhanden"
                        : "Ende nuk ka punëtorë")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {currentItems.map((item, index) => (
                  <div
                    key={item.id || index}
                    className={`flex flex-col gap-4 rounded-3xl border p-4 transition shadow-[0_12px_30px_rgba(15,23,42,0.04)] md:flex-row md:items-start md:justify-between ${
                      theme === "dark"
                        ? "border-slate-700 bg-slate-900/60 hover:bg-slate-900 hover:border-slate-600"
                        : "border-slate-200 bg-slate-50/80 hover:bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4
                          className={`text-base font-semibold ${
                            theme === "dark"
                              ? "text-slate-100"
                              : "text-slate-900"
                          }`}
                        >
                          {item.name || "-"}
                        </h4>
                        <span className={getStatusBadge(item.status)}>
                          {workerStatusLabels[item.status] ||
                            item.status ||
                            workerStatusLabels.Active}
                        </span>
                      </div>

                      <p
                        className={`mt-1 text-sm font-medium ${
                          theme === "dark"
                            ? "text-slate-300"
                            : "text-slate-700"
                        }`}
                      >
                        {workerRoleLabels[item.role] ||
                          item.role ||
                          workerRoleLabels.Worker}
                      </p>

                      <p
                        className={`mt-1 truncate text-sm ${
                          theme === "dark"
                            ? "text-slate-400"
                            : "text-slate-600"
                        }`}
                      >
                        {item.phone || t?.noDescription || "Pa përshkrim"}
                      </p>
                    </div>

                    <div className="flex flex-col items-start gap-2 md:items-end">
                      <div
                        className={`text-sm font-semibold ${
                          theme === "dark"
                            ? "text-slate-100"
                            : "text-slate-900"
                        }`}
                      >
                        {formatCurrency(item.dailyRate)} /{" "}
                        {t?.day || (isGerman ? "Tag" : "ditë")}
                      </div>

                      <div className={`text-sm ${currentTheme.muted}`}>
                        {item.startDate || "-"}
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <UIButton
                          type="button"
                          onClick={() => handleEdit(item)}
                          theme={theme}
                          variant="primary"
                          size="sm"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">
                            {t?.edit || "Ndrysho"}
                          </span>
                        </UIButton>

                        <UIButton
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          theme={theme}
                          variant="softDanger"
                          size="sm"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">
                            {t?.delete || "Fshij"}
                          </span>
                        </UIButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-slate-200/60 pt-5 dark:border-slate-700/60">
              <UIButton
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                theme={theme}
                variant="secondary"
                size="md"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{prevLabel}</span>
              </UIButton>

              <span className={`text-sm font-semibold ${currentTheme.muted}`}>
                {pageLabel} {currentPage} {ofLabel} {totalPages}
              </span>

              <UIButton
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                theme={theme}
                variant="secondary"
                size="md"
              >
                <span className="hidden sm:inline">{nextLabel}</span>
                <ChevronRight className="h-4 w-4" />
              </UIButton>
            </div>
          )}
        </PanelCard>
      </div>
    </PageShell>
  );
}